import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface TransitionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  imageSrc?: string;
  imageAlt?: string;
  confirmButtonText?: string;
  icon?: React.ReactNode;
  wideImage?: boolean;
  showValidation?: boolean;
}

const TransitionModal: React.FC<TransitionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  imageSrc = 'https://memomundi.ai/pix/StampMMD.png',
  imageAlt = 'Transition',
  confirmButtonText = 'Continuer',
  icon,
  wideImage = false,
  showValidation = false
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsProcessing(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (showValidation) {
      setIsProcessing(true);

      await new Promise(resolve => setTimeout(resolve, 1800));

      onConfirm();

      setTimeout(() => {
        setIsProcessing(false);
        onClose();
      }, 100);
    } else {
      onConfirm();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={isProcessing ? undefined : onClose}
    >
      <div
        className={`relative bg-gradient-to-br from-ivory-50 via-stone-50 to-ivory-100 rounded-2xl shadow-2xl ${wideImage ? 'max-w-6xl' : 'max-w-4xl'} w-full animate-slide-up overflow-hidden border-2 border-steel-300`}
        onClick={(e) => e.stopPropagation()}
      >
        {!isProcessing && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-steel-600 hover:text-steel-800 transition-colors z-30 bg-white/80 hover:bg-white rounded-full p-2 backdrop-blur-sm shadow-md"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        {isProcessing ? (
          <div className="flex flex-col items-center justify-center min-h-[500px] p-12">
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-copper-500/30 rounded-full blur-3xl animate-pulse" />
              <img
                src="https://memomundi.ai/pix/StampMMD.png"
                alt="Stamp MMD"
                className="relative w-48 h-48 object-contain animate-pulse-slow"
              />
            </div>
            <h3 className="text-3xl font-serif font-bold text-steel-800 mb-3">
              Prise en compte...
            </h3>
            <p className="text-lg text-steel-600 animate-pulse">
              Validation en cours par MemoMundi
            </p>
          </div>
        ) : (
          <div className={`grid grid-cols-1 ${wideImage ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} min-h-[500px]`}>
            <div className={`relative bg-gradient-to-br from-ivory-100 to-stone-100 flex items-center justify-center p-8 ${wideImage ? 'lg:col-span-2' : ''}`}>
              <img
                src={imageSrc}
                alt={imageAlt}
                className="w-full h-full object-contain border-4 border-copper-300 rounded-2xl bg-white/50 p-4 shadow-lg"
              />
            </div>

            <div className="relative flex flex-col items-center justify-center p-8 lg:p-12 text-center bg-white/40">
              {icon && (
                <div className="mb-6 relative">
                  <div className="absolute inset-0 bg-copper-500/20 rounded-full blur-2xl animate-pulse" />
                  <div className="relative">
                    {icon}
                  </div>
                </div>
              )}

              <h2 className="text-4xl font-serif font-bold text-steel-800 mb-4 leading-tight">
                {title}
              </h2>

              <p className="text-lg text-steel-700 mb-8 leading-relaxed max-w-md whitespace-pre-line">
                {message}
              </p>

              <button
                onClick={handleConfirm}
                className="group relative bg-gradient-to-r from-copper-600 to-copper-700 text-white px-8 py-3.5 rounded-lg font-semibold text-base transition-all duration-300 hover:shadow-lg hover:shadow-copper-500/30 hover:-translate-y-0.5 overflow-hidden"
              >
                <span className="relative z-10">{confirmButtonText}</span>
                <div className="absolute inset-0 bg-gradient-to-r from-copper-700 to-copper-800 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransitionModal;
